import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData, IBudgetFixFlip } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Reads and writes a single field on the Fix-Flip Budget form in the React Query cache.
 * Finds the form by type === 'Fix-Flip' and propertyGuid.
 */
export function useBudgetFixFlipField<T>(
    propertyGuid: string,
    selector: (budget: IBudgetFixFlip) => T,
    updater: (budget: IBudgetFixFlip, value: T) => IBudgetFixFlip
): [T | undefined, (value: T) => void] {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<IApplicationData, Error, T | undefined>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: (d) => {
            const form = d.information?.properties?.forms?.find(
                (f) => f.type === 'Fix-Flip' && f.propertyGuid === propertyGuid
            ) as IBudgetFixFlip | undefined;
            return form !== undefined ? selector(form) : undefined;
        },
    });

    const setValue = (value: T) => {
        if (entryGuid === undefined) return;
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid), (old) => {
            if (!old) return old;
            const forms = old.information?.properties?.forms ?? [];
            const idx = forms.findIndex(
                (f) => f.type === 'Fix-Flip' && f.propertyGuid === propertyGuid
            );
            if (idx === -1) return old;
            const form = forms[idx] as IBudgetFixFlip;
            const newForms = [...forms];
            newForms[idx] = updater(form, value);
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        forms: newForms,
                    },
                },
            };
        });
    };

    return [currentValue, setValue];
}
